import { useCallback, useEffect, useRef, useState } from "react";
import type { Lang } from "./lib/i18n";
import type { Theme } from "./lib/state";
import {
  getInitialLang,
  persistLang,
  getInitialTheme,
  persistTheme,
  getRoute,
  showSplash,
  logEvent,
  startSession,
  setSessionCareerPath,
} from "./lib/state";
import { trySilentAuth, loginPopup, msalLogout, getAccessToken, fetchUserProfile, type UserProfile } from "./lib/auth";
import { notify } from "./lib/api";
import { loadProgress, saveProgress, clearProgress } from "./lib/progress";
import { loadCareerPaths } from "./data/careerPaths";
import { loadQA } from "./data/qa";
import { loadSystems } from "./data/systems";
import { loadTeam } from "./data/team";
import { loadPolicyChapters } from "./data/policies";
import { loadWelcomeMessages } from "./data/welcomeMessages";
import { loadChecklist } from "./data/checklist";
import { getQAForPath } from "./data/qa";
import { suggestCareerPath } from "./data/careerPaths";

import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { AppHeader } from "./components/AppHeader";
import { ProgressBar } from "./components/ProgressBar";
import { CoachMark } from "./components/CoachMark";
import { HelpWidget } from "./components/HelpWidget";
import { Splash } from "./components/Splash";
import { AdminRoute } from "./admin/AdminRoute";

import { WelcomeScreen } from "./screens/WelcomeScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { PoliciesScreen } from "./screens/PoliciesScreen";
import { FaqScreen } from "./screens/FaqScreen";
import { SystemsScreen } from "./screens/SystemsScreen";
import { ChecklistScreen } from "./screens/ChecklistScreen";
import { CertificateScreen } from "./screens/CertificateScreen";

// Steps, after removing the manual "career path" pick screen (now auto-
// resolved from the employee's job title/department, see selectAutoCareerPath):
//   1 Sign in · 2 Profile · 3 Policies · 4 Department FAQ · 5 Systems map
//   6 Review checklist · 7 Journey completed
const LAST_STEP = 7;

/** Step 4 (department Q&A) has nothing to show when the admin has disabled
 *  every question, or added none, for the employee's chosen career path —
 *  in that case the step is skipped automatically rather than shown empty. */
function hasQaContent(path: string): boolean {
  return getQAForPath(path).length > 0;
}


export default function App() {
  const [lang, setLang] = useState<Lang>(getInitialLang);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [careerPath, setCareerPath] = useState("");
  const [route, setRoute] = useState(getRoute());
  const [splash, setSplash] = useState(showSplash);
  const welcomedEmails = useRef(new Set<string>());

  // Persist language & theme (and reflect on <html>).
  useEffect(() => persistLang(lang), [lang]);
  useEffect(() => persistTheme(theme), [theme]);

  // Load admin-configured content overrides once on mount.
  useEffect(() => {
    loadCareerPaths();
    loadQA();
    loadSystems();
    loadTeam();
    loadPolicyChapters();
    loadWelcomeMessages();
    loadChecklist();
  }, []);

  // React to hash / history route changes.
  useEffect(() => {
    const onRoute = () => setRoute(getRoute());
    window.addEventListener("hashchange", onRoute);
    window.addEventListener("popstate", onRoute);
    return () => {
      window.removeEventListener("hashchange", onRoute);
      window.removeEventListener("popstate", onRoute);
    };
  }, []);

  // Scroll to top when navigating.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, route]);

  // Send the welcome email once we reach the profile step.
  useEffect(() => {
    if (step !== 2 || !profile?.email || welcomedEmails.current.has(profile.email)) return;
    welcomedEmails.current.add(profile.email);
    notify("welcome", { to: profile.email, lang, vars: { NAME: profile.name || "" } })?.catch?.(() => {});
  }, [step, profile, lang]);

  /** Go to a step and persist it (plus the career path, when known) so a tab
   *  close / refresh mid-journey resumes here instead of restarting at the
   *  Profile step. `path` is optional — most calls just move between steps
   *  that don't change the career path. Step 4 (department Q&A) is skipped
   *  automatically when there's nothing to show for the target career path —
   *  forward when arriving from an earlier step, backward when arriving from
   *  a later one (e.g. a progress-bar dot jump back to review it). */
  const goToStep = useCallback(
    (s: number, path?: string) => {
      const effectivePath = path !== undefined ? path : careerPath;
      const target = s === 4 && !hasQaContent(effectivePath) ? (step < 4 ? 5 : 3) : s;
      setStep(target);
      const email = profile?.email || (typeof window !== "undefined" && sessionStorage.getItem("kd_email")) || "";
      if (email) saveProgress(email, { step: target, ...(path !== undefined ? { careerPath: path } : {}) });
    },
    [profile, step, careerPath],
  );

  /** After a fresh profile fetch (silent or interactive), resume the saved
   *  step/career path for this email if any exists, otherwise start at the
   *  Profile step as usual. */
  function resumeOrStart(email: string) {
    const saved = email ? loadProgress(email) : null;
    if (saved && saved.step > 1 && saved.step < LAST_STEP) {
      const resumeStep = saved.step === 4 && !hasQaContent(saved.careerPath || "") ? 5 : saved.step;
      setStep(resumeStep);
      if (saved.careerPath) setCareerPath(saved.careerPath);
    } else {
      setStep(2);
    }
  }

  /** Auto-resolve the employee's career path from their job title/department
   *  (already fetched from Microsoft Graph into profile.role — see auth.ts)
   *  instead of asking them to pick one on a dedicated screen, which added a
   *  decision point that mostly just delayed the journey without changing
   *  what employees actually saw (every system is still shown either way,
   *  just filed under "core" vs "other systems"). No confident match leaves
   *  the path unset, which the rest of the journey already treats gracefully. */
  function selectAutoCareerPath(p: UserProfile | null, email: string) {
    const path = suggestCareerPath(p?.role || "") || "";
    if (!path) return;
    setCareerPath(path);
    setSessionCareerPath(email, path);
    logEvent("career_selected", { email, name: p?.name || "", meta: { path, auto: true } });
  }

  // Silent re-authentication on load.
  useEffect(() => {
    (async () => {
      const { authed } = await trySilentAuth();
      if (!authed) return;
      setProfileLoading(true);
      const token = await getAccessToken();
      if (token) {
        const p = await fetchUserProfile(token);
        setProfile(p);
        if (p?.name) {
          const email = p.email || "";
          startSession({ email, name: p.name, role: p.role });
          logEvent("login", { email, name: p.name });
          selectAutoCareerPath(p, email);
          resumeOrStart(email);
        } else {
          setStep(2);
        }
      } else {
        setStep(2);
      }
      setProfileLoading(false);
    })();
  }, []);

  const toggleLang = useCallback(() => setLang((l) => (l === "ar" ? "en" : "ar")), []);
  const toggleTheme = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);

  const handleLogin = useCallback(async () => {
    setLoginLoading(true);
    setLoginError(null);
    try {
      const account = await loginPopup();
      if (account) {
        setProfileLoading(true);
        const token = await getAccessToken();
        if (token) {
          const p = await fetchUserProfile(token);
          setProfile(p);
          const email = account.username || "";
          startSession({ email, name: p?.name || account.name || "", role: p?.role || "" });
          logEvent("login", { email, name: p?.name || account.name || "" });
          selectAutoCareerPath(p, email);
          resumeOrStart(email);
        } else {
          setStep(2);
        }
        setProfileLoading(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Sign-in failed";
      if (!msg.includes("user_cancelled") && !msg.includes("popup_window_error")) {
        setLoginError(
          lang === "ar" ? "تعذّر تسجيل الدخول. يُرجى المحاولة مرة أخرى أو التواصل مع قسم تقنية المعلومات." : msg,
        );
      }
    } finally {
      setLoginLoading(false);
    }
  }, [lang]);

  // --- Splash ---
  if (splash) {
    return (
      <div className={`kd-app ${theme}`}>
        <Splash theme={theme} lang={lang} onDone={() => setSplash(false)} />
      </div>
    );
  }

  // --- Admin route ---
  if (route === "admin") {
    return (
      <div className={`kd-app ${theme}`}>
        <AppHeader lang={lang} theme={theme} onToggleLang={toggleLang} onToggleTheme={toggleTheme} />
        <AdminRoute lang={lang} />
        <Toaster position={lang === "ar" ? "bottom-left" : "bottom-right"} theme={theme} richColors closeButton />
      </div>
    );
  }

  // --- Onboarding wizard ---
  const renderStep = () => {
    switch (step) {
      case 1:
        return <WelcomeScreen lang={lang} onLogin={handleLogin} loading={loginLoading} error={loginError} />;
      case 2:
        return <ProfileScreen lang={lang} profile={profile} loading={profileLoading} onNext={() => goToStep(3)} />;
      case 3:
        return <PoliciesScreen lang={lang} onNext={() => goToStep(4)} onBack={() => goToStep(2)} />;
      case 4:
        return <FaqScreen lang={lang} careerPath={careerPath} onNext={() => goToStep(5)} onBack={() => goToStep(3)} />;
      case 5:
        return <SystemsScreen lang={lang} careerPath={careerPath} onNext={() => goToStep(6)} onBack={() => goToStep(4)} />;
      case 6:
        return <ChecklistScreen lang={lang} onNext={() => goToStep(7)} onBack={() => goToStep(5)} />;
      case 7:
        return (
          <CertificateScreen
            lang={lang}
            profile={profile}
            careerPath={careerPath}
            onRestart={() => {
              const email = profile?.email || (typeof window !== "undefined" && sessionStorage.getItem("kd_email")) || "";
              if (email) clearProgress(email);
              setCareerPath("");
              selectAutoCareerPath(profile, email);
              // Not step 1 — the MSAL session is still valid, so re-showing
              // the login screen (and its "Sign in" button) would be both
              // pointless and confusing for an already-authenticated user.
              goToStep(2);
            }}
            onBack={() => goToStep(6)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`kd-app ${theme}`}>
      <AppHeader
        lang={lang}
        theme={theme}
        profile={profile}
        onToggleLang={toggleLang}
        onToggleTheme={toggleTheme}
        onSignOut={async () => {
          await msalLogout();
          setProfile(null);
          setStep(1);
        }}
      />
      <main className="kd-main">
        <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
        <CoachMark step={step} lang={lang} />
      </main>
      <ProgressBar step={step} lang={lang} onJumpToStep={(s) => goToStep(s)} />
      <HelpWidget lang={lang} />
      <Toaster position={lang === "ar" ? "bottom-left" : "bottom-right"} theme={theme} richColors closeButton />
    </div>
  );
}
